
import { useState, useRef, useCallback, useEffect, startTransition } from 'react';
import { Device } from '@twilio/voice-sdk';
import { initializeDevice, getDevice, destroyDevice } from '@/services/twilio/voice/deviceManager';
import { logger } from '@/utils/logger';

export const useTwilioDevice = (isTwilioConfigured: boolean) => {
  const [isInitializingDevices, setIsInitializingDevices] = useState(false);
  const deviceRef = useRef<Device | null>(null);

  // Initialize device using the existing deviceManager service
  const initializeDeviceInstance = useCallback(async (): Promise<Device | null> => {
    if (!isTwilioConfigured) {
      logger.warn('🚫 Twilio not configured, skipping device initialization');
      return null;
    }

    // Check if device is already initialized
    const existingDevice = getDevice();
    if (existingDevice && existingDevice.state === 'registered') {
      logger.debug('📱 Using existing registered device');
      deviceRef.current = existingDevice;
      return existingDevice;
    }

    try {
      logger.debug('🔧 Initializing Twilio device using deviceManager...');
      setIsInitializingDevices(true);
      
      const result = await initializeDevice();
      
      if (result.success && result.device) {
        deviceRef.current = result.device;
        logger.debug('✅ Device initialized successfully');
        return result.device;
      } else {
        logger.error('❌ Device initialization failed', result.error as any);
        throw new Error(result.error || 'Device initialization failed');
      }
    } catch (error) {
      logger.error('❌ Error initializing device', error as any);
      throw error;
    } finally {
      startTransition(() => {
        setIsInitializingDevices(false);
      });
    }
  }, [isTwilioConfigured]);

  // Handle token expiry by reinitializing the device
  const handleTokenExpiry = useCallback(async () => {
    logger.debug('🔄 Token expired, reinitializing device...');
    try {
      // Destroy existing device first
      destroyDevice();
      deviceRef.current = null;
      
      // Reinitialize with fresh token
      return await initializeDeviceInstance();
    } catch (error) {
      logger.error('❌ Failed to handle token expiry', error as any);
      throw error;
    }
  }, [initializeDeviceInstance]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      logger.debug('🧹 useTwilioDevice unmounting. Clearing local refs only.');
      deviceRef.current = null;
    };
  }, []);

  return {
    deviceRef,
    isInitializingDevices,
    initializeDevice: initializeDeviceInstance,
    handleTokenExpiry
  };
};
